﻿// Общий слой интерфейса, не зависящий от фреймворка: полоса уведомлений,
// счётчик корзины, модальные окна, согласия перед оплатой и подсветка
// матрицы локализации.
//
// Раньше всё это жило в $rootScope AngularJS. Вынесено сюда, чтобы
// Razor-компоненты могли пользоваться этим через JS-interop.

window.zs = window.zs || ({} as Zs);

// --- Полоса уведомлений -----------------------------------------------------
// Полоса не прячется по таймеру: сообщение остаётся, пока его не закроют
// (WCAG 2.2.1 — у читающего должно быть столько времени, сколько нужно).
// Разметка несёт role="status", поэтому новый текст зачитывается сам.

function notificationBar(): HTMLElement | null {
    return document.querySelector<HTMLElement>("[data-notification]");
}

window.zs.notify = {
    show: function (type: string, title: string, message: string): void {
        const bar = notificationBar();
        if (!bar) {
            return;
        }

        const body = bar.querySelector("[data-notification-body]");
        const titleEl = bar.querySelector("[data-notification-title]");
        const messageEl = bar.querySelector("[data-notification-message]");

        if (titleEl) {
            titleEl.textContent = title || "";
        }
        if (messageEl) {
            messageEl.textContent = message || "";
        }
        if (body) {
            body.className = "notification notification-" + (type || "info");
        }

        bar.classList.remove("inactive");
        bar.classList.add("active");
    },

    dismiss: function (): void {
        const bar = notificationBar();
        if (!bar) {
            return;
        }

        bar.classList.remove("active");
        bar.classList.add("inactive");
    }
};

// --- Счётчик корзины --------------------------------------------------------

window.zs.cart = {
    // Начальное значение приходит с сервера; обновляется после того,
    // как товар добавлен в корзину без перезагрузки страницы.
    setCount: function (count: number): void {
        document.querySelectorAll("[data-cart-count]").forEach(function (badge) {
            badge.textContent = String(count);
        });

        // Ссылка на корзину называется «Корзина: N» — число входит в имя,
        // потому что сам бейдж скрыт от вспомогательных технологий.
        document.querySelectorAll("[data-cart-label]").forEach(function (link) {
            link.setAttribute("aria-label", link.getAttribute("data-cart-label") + ": " + count);
        });
    }
};

// --- Модальные окна ---------------------------------------------------------
// Заменяют единственное, ради чего в проект тянулся Bootstrap JS (92 КБ на
// каждой странице): collapse, offcanvas и липкую шапку делает script.core,
// а всё остальное из библиотеки не использовалось. Оформление живёт в
// Styles/Components/_modal.scss, здесь только переключение классов.
//
// Разметка не меняется — те же data-bs-toggle / data-bs-target /
// data-bs-dismiss, что понимал Bootstrap.

const MODAL_TRANSITION_MS = 300;

let openModal: HTMLElement | null = null;
let modalBackdrop: HTMLElement | null = null;

// Элемент, с которого открыли окно: фокус возвращается на него при закрытии.
let modalOpener: HTMLElement | null = null;

const MODAL_FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function modalFocusable(modal: HTMLElement): HTMLElement[] {
    return Array.prototype.filter.call(
        modal.querySelectorAll<HTMLElement>(MODAL_FOCUSABLE),
        function (el: HTMLElement) {
            return el.getClientRects().length > 0;
        });
}

// Tab внутри открытого окна ходит по кругу и не выпускает фокус на страницу.
function modalTrapFocus(event: KeyboardEvent, modal: HTMLElement): void {
    const items = modalFocusable(modal);

    if (!items.length) {
        event.preventDefault();
        modal.focus();
        return;
    }

    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    const inside = active instanceof Node && modal.contains(active);

    if (event.shiftKey && (active === first || !inside)) {
        event.preventDefault();
        last.focus();
    } else if (!event.shiftKey && (active === last || !inside)) {
        event.preventDefault();
        first.focus();
    }
}

window.zs.modal = {
    show: function (target: string | Element | null): void {
        const el = typeof target === "string" ? document.querySelector(target) : target;

        if (!(el instanceof HTMLElement) || openModal === el) {
            return;
        }

        if (openModal) {
            window.zs.modal.hide();
        } else {
            // Запоминаем только при открытии первого окна: если одно окно
            // сменяет другое, вернуться нужно к исходной кнопке.
            modalOpener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        }

        // Само окно должно уметь принимать фокус — на случай, если внутри
        // нет ни одного интерактивного элемента.
        if (!el.hasAttribute("tabindex")) {
            el.tabIndex = -1;
        }

        // Полоса прокрутки исчезает вместе с прокруткой страницы —
        // компенсируем её ширину, иначе содержимое дёргается вбок.
        const scrollbar = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.paddingRight = scrollbar > 0 ? scrollbar + "px" : "";
        document.body.classList.add("modal-open");

        const backdrop = document.createElement("div");
        backdrop.className = "modal-backdrop fade";
        document.body.appendChild(backdrop);
        modalBackdrop = backdrop;

        el.style.display = "block";
        el.removeAttribute("aria-hidden");
        el.setAttribute("aria-modal", "true");

        // Перерисовка нужна, чтобы переход по opacity действительно проиграл.
        void el.offsetWidth;

        backdrop.classList.add("show");
        el.classList.add("show");

        openModal = el;

        // Фокус переносится после анимации: если сделать это сразу,
        // браузер вернёт его на нажатую кнопку по завершении щелчка.
        window.setTimeout(function () {
            if (openModal !== el) {
                return;
            }

            const focusable = el.querySelector<HTMLElement>(
                "input, textarea, select, button:not([data-bs-dismiss])");

            (focusable || el).focus();
        }, MODAL_TRANSITION_MS);
    },

    hide: function (): void {
        const el = openModal;
        if (!el) {
            return;
        }

        const backdrop = modalBackdrop;

        el.classList.remove("show");
        if (backdrop) {
            backdrop.classList.remove("show");
        }

        openModal = null;
        modalBackdrop = null;

        window.setTimeout(function () {
            el.style.display = "";
            el.setAttribute("aria-hidden", "true");
            el.removeAttribute("aria-modal");

            if (backdrop && backdrop.parentNode) {
                backdrop.parentNode.removeChild(backdrop);
            }

            // Другое окно могли открыть, пока это закрывалось.
            if (!openModal) {
                document.body.classList.remove("modal-open");
                document.body.style.paddingRight = "";

                // Фокус — туда, откуда окно открыли (если элемент ещё на странице).
                if (modalOpener && modalOpener.isConnected) {
                    modalOpener.focus();
                }

                modalOpener = null;
            }
        }, MODAL_TRANSITION_MS);
    },

    init: function (): void {
        // Обработчики вешаются на документ: окна и кнопки внутри
        // Razor-компонентов появляются в DOM уже после загрузки страницы.
        document.addEventListener("click", function (event) {
            const target = event.target;

            if (!(target instanceof Element)) {
                return;
            }

            const opener = target.closest("[data-bs-toggle='modal']");
            if (opener) {
                if (opener.tagName === "A") {
                    event.preventDefault();
                }
                window.zs.modal.show(opener.getAttribute("data-bs-target"));
                return;
            }

            if (target.closest("[data-bs-dismiss='modal']")) {
                window.zs.modal.hide();
                return;
            }

            // Щелчок мимо окна закрывает его.
            if (openModal && target === openModal) {
                window.zs.modal.hide();
            }
        });

        document.addEventListener("keydown", function (event) {
            if (!openModal) {
                return;
            }

            if (event.key === "Escape") {
                window.zs.modal.hide();
            } else if (event.key === "Tab") {
                modalTrapFocus(event, openModal);
            }
        });
    }
};

// --- Кнопка «Копировать» у блоков кода ---------------------------------------
// Прогрессивное улучшение: без JS (или без Clipboard API) блок кода остаётся
// как есть. HTML приходит из Markdown, поэтому кнопка добавляется скриптом ко
// всем <pre><code> внутри <main>. Подписи берутся из data-label-* на <main>
// (см. _Layout.cshtml), результат объявляется через aria-live.

const COPY_RESET_MS = 2000;

function initCodeCopy(): void {
    const main = document.querySelector<HTMLElement>("main");
    const blocks = document.querySelectorAll<HTMLElement>("main pre > code, .article pre > code");

    if (!main || !blocks.length || !navigator.clipboard) {
        return;
    }

    const copyLabel = main.getAttribute("data-label-copy") || "Copy";
    const copiedLabel = main.getAttribute("data-label-copied") || "Copied";

    const live = document.createElement("div");
    live.className = "sr-only";
    live.setAttribute("aria-live", "polite");
    document.body.appendChild(live);

    blocks.forEach(function (code) {
        const pre = code.parentElement;

        if (!(pre instanceof HTMLElement) || pre.parentElement?.classList.contains("code-block")) {
            return;
        }

        // Обёртка нужна, чтобы кнопка не уезжала вместе с горизонтальной
        // прокруткой длинных строк внутри <pre>.
        const wrapper = document.createElement("div");
        wrapper.className = "code-block";
        pre.parentNode?.insertBefore(wrapper, pre);
        wrapper.appendChild(pre);

        const button = document.createElement("button");
        button.type = "button";
        button.className = "code-copy";
        button.textContent = copyLabel;
        button.setAttribute("aria-label", copyLabel);
        wrapper.appendChild(button);

        let timer = 0;

        button.addEventListener("click", function () {
            navigator.clipboard.writeText(code.textContent || "").then(function () {
                button.textContent = copiedLabel;
                button.classList.add("is-copied");
                live.textContent = copiedLabel;

                window.clearTimeout(timer);
                timer = window.setTimeout(function () {
                    button.textContent = copyLabel;
                    button.classList.remove("is-copied");
                    live.textContent = "";
                }, COPY_RESET_MS);
            }, function () {
                // Буфер недоступен (например, страница не в безопасном
                // контексте) — кнопка просто ничего не меняет.
            });
        });
    });
}

// --- Согласия перед оплатой -------------------------------------------------
// Ссылка остаётся заблокированной, пока не отмечены все чекбоксы группы:
//   <input type="checkbox" data-consent="ГРУППА">
//   <a data-consent-gate="ГРУППА" data-consent-href="/куда">
//   <p data-consent-hint="ГРУППА">почему кнопка не нажимается</p>
//
// Подсказка необязательна, но без неё заблокированная кнопка ничем не
// объясняет, чего от человека ждут.

window.zs.consent = {
    init: function (): void {
        document.querySelectorAll("[data-consent-gate]").forEach(function (gate) {
            const group = gate.getAttribute("data-consent-gate");
            const boxes = document.querySelectorAll<HTMLInputElement>(
                '[data-consent="' + group + '"]');
            const hint = document.querySelector<HTMLElement>(
                '[data-consent-hint="' + group + '"]');

            if (boxes.length === 0) {
                return;
            }

            const sync = function (): void {
                let accepted = true;

                boxes.forEach(function (box) {
                    accepted = accepted && box.checked;
                });

                gate.classList.toggle("disabled", !accepted);
                gate.setAttribute("aria-disabled", accepted ? "false" : "true");

                const href = gate.getAttribute("data-consent-href");

                if (accepted && href) {
                    gate.setAttribute("href", href);
                } else {
                    gate.removeAttribute("href");
                }

                if (hint) {
                    hint.hidden = accepted;
                }
            };

            boxes.forEach(function (box) { box.addEventListener("change", sync); });
            sync();
        });
    }
};

// --- Матрица локализации ----------------------------------------------------
// Наведение на любую ячейку продукта выделяет все ячейки того же продукта.

window.zs.matrix = {
    init: function (): void {
        const cells = document.querySelectorAll("[data-prod]");
        if (cells.length === 0) {
            return;
        }

        const highlight = function (value: string | null): void {
            cells.forEach(function (cell) {
                cell.classList.toggle("selected",
                    value !== null && cell.getAttribute("data-prod") === value);
            });
        };

        cells.forEach(function (cell) {
            cell.addEventListener("mouseenter", function () {
                highlight(cell.getAttribute("data-prod"));
            });
        });

        const matrix = document.querySelector(".localization");
        if (matrix) {
            matrix.addEventListener("mouseleave", function () {
                highlight(null);
            });
        }
    }
};

// --- Согласие на cookie -----------------------------------------------------
// Разметка: Pages/Shared/_CookieConsent.cshtml (скрыта атрибутом hidden).
// Выбор хранится в cookie zs_consent на год: "all" — можно ставить cookie
// аналитики (Application Insights: ai_user, ai_session), "necessary" — нет.
// Сниппет App Insights в _Scripts читает этот cookie при загрузке страницы
// (disableCookiesUsage), а здесь переключение применяется ещё и на лету.

window.zs.cookieConsent = (function (): ZsCookieConsent {
    const cookieName = "zs_consent";
    const maxAge = 60 * 60 * 24 * 365;
    const analyticsCookies = ["ai_user", "ai_session"];

    function banner(): HTMLElement | null {
        return document.querySelector<HTMLElement>("[data-cookie-consent]");
    }

    function get(): "all" | "necessary" | null {
        const match = document.cookie.match(/(?:^|;\s*)zs_consent=(all|necessary)(?:;|$)/);
        return match ? (match[1] as "all" | "necessary") : null;
    }

    function applyAnalytics(enabled: boolean): void {
        const sdk = window.appInsights;
        if (sdk && typeof sdk.getCookieMgr === "function") {
            try {
                sdk.getCookieMgr().setEnabled(enabled);
            } catch {
                // SDK ещё не поднялся — настройка применится при следующей загрузке.
            }
        }

        if (!enabled) {
            // Удаляем уже поставленные cookie аналитики: и на хосте, и на домене.
            const host = location.hostname;
            const domain = host.split(".").slice(-2).join(".");
            analyticsCookies.forEach(function (name) {
                document.cookie = name + "=; path=/; max-age=0";
                if (domain !== host) {
                    document.cookie = name + "=; path=/; domain=." + domain + "; max-age=0";
                }
            });
        }
    }

    function set(choice: "all" | "necessary"): void {
        const secure = location.protocol === "https:" ? "; Secure" : "";
        document.cookie = cookieName + "=" + choice + "; path=/; max-age=" + maxAge + "; SameSite=Lax" + secure;

        applyAnalytics(choice === "all");

        const el = banner();
        if (el) {
            el.hidden = true;
        }

        document.dispatchEvent(new CustomEvent("zs:cookie-consent", { detail: { choice: choice, analytics: choice === "all" } }));
    }

    function open(): void {
        const el = banner();
        if (!el) {
            return;
        }

        el.hidden = false;
        const first = el.querySelector<HTMLElement>("[data-cookie-consent-choice]");
        if (first) {
            first.focus();
        }
    }

    function init(): void {
        const el = banner();
        if (!el) {
            return;
        }

        el.querySelectorAll<HTMLElement>("[data-cookie-consent-choice]").forEach(function (button) {
            button.addEventListener("click", function () {
                const choice = button.getAttribute("data-cookie-consent-choice");
                if (choice === "all" || choice === "necessary") {
                    set(choice);
                }
            });
        });

        document.querySelectorAll<HTMLElement>("[data-cookie-settings]").forEach(function (link) {
            link.addEventListener("click", function (event) {
                event.preventDefault();
                open();
            });
        });

        // Без ответа баннер показывается, но фокус не забирает — это не модальное окно.
        if (get() === null) {
            el.hidden = false;
        }
    }

    return { init: init, open: open, get: get, set: set };
})();

// --- Запуск -----------------------------------------------------------------

(function () {
    const start = function (): void {
        const close = document.querySelector("[data-notification-close]");
        if (close) {
            close.addEventListener("click", window.zs.notify.dismiss);
        }

        // Кнопки «Назад» / «Повторить» на страницах ошибок: JS-URL в href
        // запретила бы политика безопасности, поэтому обработчик здесь.
        document.querySelectorAll("[data-history-back]").forEach(function (link) {
            link.addEventListener("click", function (event) {
                if (window.history.length > 1) {
                    event.preventDefault();
                    window.history.back();
                }
            });
        });

        window.zs.matrix.init();
        window.zs.consent.init();
        window.zs.cookieConsent.init();
        window.zs.modal.init();
        initCodeCopy();
    };

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", start);
    } else {
        start();
    }
})();
